import React from 'react';
import axios from 'axios';
import { T, MONO } from './poyais-ui/theme';
import { Icon } from './poyais-ui/icons';
import { useIsMobile } from './poyais-ui/hooks';
import { isBrandCampaign } from './poyais-ui/format';
import { useLang, addTranslations } from './i18n';
import PoyaisSpinner from './PoyaisSpinner';

addTranslations({
  'Yenile': 'Refresh',
  'Yenileniyor…': 'Refreshing…',
  'Öneriler yenilendi. Poyais One AI önerileri arka planda hazırlanıyor, birkaç dakika sonra tekrar yenileyebilirsin.': 'Recommendations refreshed. Poyais One AI is preparing more in the background; refresh again in a few minutes.',
  'Yenilenemedi.': 'Could not refresh.',
  'Poyais One AI tarafından incelendi': 'Reviewed by Poyais One AI',
  'Açıklama Poyais One AI tarafından yazıldı': 'Explanation written by Poyais One AI',
  'Yüksek': 'High', 'Orta': 'Medium', 'Düşük': 'Low',
  'ANALİZ EDİLİYOR': 'ANALYZING',
  'Ort. ROAS': 'Avg. ROAS', 'Toplam Harcama': 'Total Spend', 'Aktif Öneri': 'Active Suggestions', 'Uygulanan': 'Applied',
  'Genel': 'Overall', 'Marka': 'Brand', 'Marka Dışı': 'Non-Brand',
  'Öneriler': 'Suggestions', 'Onay Bekleyenler': 'Pending Approval',
  'Tümü': 'All', 'Google': 'Google', 'Meta': 'Meta',
  'Bekleyen': 'Pending', 'Reddedilen': 'Rejected',
  'Öneri': 'suggestions',
  'Geçmiş Etki': 'Last Impact', 'iyileşti': 'improved', 'ölçüm': 'measurements', 'tüm müşteriler': 'all clients',
  'Öneri bulunamadı': 'No suggestions found',
  'Bu filtre için görüntülenecek öneri yok.': 'There are no suggestions to show for this filter.',
  'Öncelik': 'Priority',
  'Onaya Gönderildi': 'Sent for Approval',
  'Reddedildi': 'Rejected',
  'Açıklama': 'Description',
  'Ne Yapacak?': 'What Will Happen?',
  'AI Kreatif Önerisi': 'AI Creative Suggestion',
  'Kopyala': 'Copy',
  'Yeniden Üret': 'Regenerate',
  'Kreatif Önerisi Üret': 'Generate Creative Suggestion',
  'Reddet': 'Reject',
  'Uygula': 'Apply',
  'Geri Al': 'Undo',
  'Toplam Tahmini Etki': 'Total Estimated Impact',
  'uygulandı': 'applied',
  'Onay kuyruğuna eklendi. Optimizasyonlar → Onay Bekleyenler sekmesinden onaylayabilirsin.': 'Added to the approval queue. You can approve it from the Optimization → Pending Approval tab.',
  'Uygulama sırasında hata oluştu.': 'Something went wrong while applying this.',
  'Öneri reddedildi.': 'Suggestion rejected.',
  'Üretim sırasında hata oluştu.': 'Something went wrong while generating this.',
  'Panoya kopyalandı.': 'Copied to clipboard.',
  'Potansiyel Tasarruf': 'Potential Savings',
  'Tahmini Etki': 'Estimated Impact',
  // PendingApprovals
  'Bütçe Artışı': 'Budget Increase', 'Bütçe Azaltma': 'Budget Decrease', 'Kampanya Duraklat': 'Pause Campaign',
  'Reklam Duraklat': 'Pause Ad', 'Kampanya Etkinleştir': 'Enable Campaign', 'Hedef ROAS Güncelle': 'Update Target ROAS',
  'Yükleniyor…': 'Loading…',
  'Onay bekleyen öneri yok': 'No suggestions pending approval',
  'Öneriler sekmesinden bir aksiyon uyguladığında burada onay bekleyecek.': "When you apply an action from the Suggestions tab, it will wait for approval here.",
  'Optimizasyon önerisi': 'Optimization suggestion',
  'Onaylandı ve uygulandı.': 'Approved and applied.',
  'İşlem sırasında hata oluştu.': 'Something went wrong.',
  'Onayla': 'Approve',
  'Bütçe Aktarımı': 'Budget Reallocation',
  'Kaynak': 'From',
  'Hedef': 'To',
});

const API = '/api/v1';

const PRIO: Record<string,{label:string;bg:string;fg:string}> = {
  high:   { label:'Yüksek', bg:'var(--py-red-tint)', fg:T.redText },
  medium: { label:'Orta',   bg:'var(--py-amber-tint-alt)', fg:T.amberText },
  low:    { label:'Düşük',  bg:'var(--py-green-tint)', fg:T.greenText },
};
const PLAT: Record<string,{label:string;bg:string;fg:string}> = {
  google:   { label:'Google Ads', bg:'var(--py-amber-tint-alt)', fg:T.accentDeep },
  meta:     { label:'Meta Ads',   bg:'var(--py-purple-tint)', fg:T.purpleText },
  ga4:      { label:'GA4',        bg:'var(--py-orange-tint)', fg:T.orangeText },
};

const fmt = (v: number) => v >= 1000000 ? `₺${(v/1000000).toFixed(1)}M` : v >= 1000 ? `₺${(v/1000).toFixed(0)}K` : `₺${v}`;

function Pill({ bg, fg, children }: { bg:string; fg:string; children:React.ReactNode }) {
  return <span style={{ fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:999, background:bg, color:fg }}>{children}</span>;
}

export default function RecommendationsTab({ config, start, end }: { config: any; start?: string; end?: string }) {
  const { t, lang } = useLang();
  const [recs, setRecs]                 = React.useState<any[]>([]);
  const [loading, setLoading]           = React.useState(true);
  const [platFilter, setPlatFilter]     = React.useState('all');
  const [statusFilter, setStatusFilter] = React.useState('pending');
  const [applying, setApplying]         = React.useState<string|null>(null);
  const [toast, setToast]               = React.useState<{msg:string;ok:boolean}|null>(null);
  const [blendedRoas, setBlendedRoas]   = React.useState(0);
  const [blendedSpend, setBlendedSpend] = React.useState(0);
  // Brand/NonBrand/Overall ROAS segmentasyonu — poyais-ui/format.ts'teki isBrandCampaign
  // ile aynı kanonik liste, DashboardTab.tsx'teki eşdeğeriyle senkron. Tool özelliği: tüm
  // müşteriler için geçerli (kullanıcı isteği, 2026-09-13) — "Ort. ROAS'a branding
  // kampanyaları da dahil oluyor, yanlış" şikayeti üzerine.
  const [roasSegment, setRoasSegment]   = React.useState<'overall'|'brand'|'nonbrand'>('overall');
  const [roasMenuOpen, setRoasMenuOpen] = React.useState(false);
  const [brandSpend, setBrandSpend]     = React.useState(0);
  const [brandRevenue, setBrandRevenue] = React.useState(0);
  const [nonBrandSpend, setNonBrandSpend]     = React.useState(0);
  const [nonBrandRevenue, setNonBrandRevenue] = React.useState(0);
  const [expanded, setExpanded]         = React.useState<string|null>(null);
  const [view, setView]                 = React.useState<'oneriler'|'onay'>('oneriler');
  const [pendingLogsCount, setPendingLogsCount] = React.useState(0);
  const isMobile = useIsMobile();
  const clientId = config?.client_id || config?.activeClientId || 1;

  const showToast = (msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 4000);
  };

  React.useEffect(() => {
    const metaId = config?.platforms?.meta?.accountId;
    const gadsId = config?.platforms?.gads?.customerId;
    const end2   = end   || new Date().toISOString().slice(0,10);
    const start2 = start || new Date(Date.now()-30*86400000).toISOString().slice(0,10);
    Promise.all([
      metaId ? axios.get(`${API}/meta/${metaId}/overview?start_date=${start2}&end_date=${end2}`).catch(()=>null) : null,
      gadsId ? axios.get(`${API}/gads/${gadsId}/overview?start_date=${start2}&end_date=${end2}`).catch(()=>null) : null,
      metaId ? axios.get(`${API}/meta/${metaId}/campaigns?start_date=${start2}&end_date=${end2}`).catch(()=>null) : null,
      gadsId ? axios.get(`${API}/gads/${gadsId}/campaigns?start_date=${start2}&end_date=${end2}`).catch(()=>null) : null,
    ]).then(([mR, gR, mCampR, gCampR]) => {
      const mo = mR?.data?.overview || mR?.data || {};
      const go = gR?.data || {};
      const ts = (mo.spend||0) + (go.spend||0);
      const tr = (mo.revenue||0) + (go.revenue||go.conversions_value||0);
      setBlendedSpend(ts);
      setBlendedRoas(ts > 0 ? tr/ts : 0);

      const mCamps = Array.isArray(mCampR?.data) ? mCampR?.data : (mCampR?.data?.campaigns || []);
      const gCamps = Array.isArray(gCampR?.data) ? gCampR?.data : (gCampR?.data?.campaigns || []);
      const allCamps = [
        ...gCamps.map((c:any)=>({name:c.name||c.campaign_name||'—', spend:c.spend||0, revenue:c.revenue??(c.spend&&c.roas?c.spend*c.roas:0)})),
        ...mCamps.map((c:any)=>({name:c.name||c.campaign_name||'—', spend:c.spend||0, revenue:c.revenue??(c.spend&&c.roas?c.spend*c.roas:0)})),
      ];
      const brandCamps = allCamps.filter(c=>isBrandCampaign(c.name));
      const nonBrandCamps = allCamps.filter(c=>!isBrandCampaign(c.name));
      setBrandSpend(brandCamps.reduce((s,c)=>s+c.spend,0));
      setBrandRevenue(brandCamps.reduce((s,c)=>s+c.revenue,0));
      setNonBrandSpend(nonBrandCamps.reduce((s,c)=>s+c.spend,0));
      setNonBrandRevenue(nonBrandCamps.reduce((s,c)=>s+c.revenue,0));
    });
  }, [config, start, end]);

  React.useEffect(() => {
    setLoading(true);
    // Hesap bağlı değilse boş gönderilir, backend o platformu atlar. (Eskiden sabit bir
    // Google Ads/Meta hesabına düşülüyordu; bağlantısız müşteride başka hesabın verisi görünüyordu.)
    const gadsId = config?.platforms?.gads?.customerId || '';
    const metaId = config?.platforms?.meta?.accountId  || '';
    axios.get(`${API}/optimize/recommendations`, { params: { customer_id: gadsId, ad_account_id: metaId, client_id: clientId, days: 30, lang } })
      .then(res => {
        const data = Array.isArray(res.data) ? res.data : (res.data?.recommendations || []);
        setRecs(data.map((r: any, i: number) => ({
          ...r,
          id:           r.id           || `rec_${i}`,
          status:       'pending',
          priority:     r.priority     || 'medium',
          impact:       r.impact       || (r.impact_value ? fmt(r.impact_value) : '—'),
          impact_label: r.impact_label || (r.impact_type === 'saving' ? t('Potansiyel Tasarruf') : t('Tahmini Etki')),
        })));
      })
      .catch(() => setRecs([]))
      .finally(() => setLoading(false));
  }, [config, lang]);

  // Manuel yenile: kural önerileri hemen, Poyais One AI önerileri arka planda (birkaç dk) güncellenir
  const [refreshing, setRefreshing] = React.useState(false);
  const refreshRecs = () => {
    setRefreshing(true);
    const gadsId = config?.platforms?.gads?.customerId || '';
    const metaId = config?.platforms?.meta?.accountId  || '';
    axios.get(`${API}/optimize/recommendations`, { params: { customer_id: gadsId, ad_account_id: metaId, client_id: clientId, days: 30, lang, refresh: true } })
      .then(res => {
        const data = Array.isArray(res.data) ? res.data : (res.data?.recommendations || []);
        setRecs(data.map((r: any, i: number) => ({ ...r, id: r.id || `rec_${i}`, status: 'pending', priority: r.priority || 'medium', impact: r.impact || (r.impact_value ? fmt(r.impact_value) : '—'),
          impact_label: r.impact_label || (r.impact_type === 'saving' ? t('Potansiyel Tasarruf') : t('Tahmini Etki')) })));
        showToast(t('Öneriler yenilendi. Poyais One AI önerileri arka planda hazırlanıyor, birkaç dakika sonra tekrar yenileyebilirsin.'));
      })
      .catch(() => showToast(t('Yenilenemedi.'), false))
      .finally(() => setRefreshing(false));
  };

  // "Onay Bekleyenler" rozeti — yalnızca bu sayfanın kendi (Optimizasyonlar > Uygula) kayıtları;
  // Otomasyon Şablonları'nın kural bazlı onayları kendi sekmesinde ayrıca sayılıyor
  const refreshPendingCount = React.useCallback(() => {
    axios.get(`${API}/automation/logs`, { params: { client_id: clientId, status: 'pending', limit: 100, has_rule: false } })
      .then(r => setPendingLogsCount(Array.isArray(r.data) ? r.data.length : 0))
      .catch(() => {});
  }, [clientId]);
  React.useEffect(() => { refreshPendingCount(); }, [refreshPendingCount, view]);

  const handleApply = async (id: string) => {
    const rec = recs.find(r => r.id === id);
    if (!rec || !rec.action?.type) return;
    setApplying(id);
    try {
      const res = await axios.post(`${API}/optimize/apply`, {
        client_id: clientId,
        platform: rec.platform || 'google',
        campaign_id: rec.action.campaign_id || rec.campaign_id || '',
        campaign_name: rec.campaign_name || '',
        action: rec.action,
        customer_id: config?.platforms?.gads?.customerId || '',
        account_id:  config?.platforms?.meta?.accountId  || '',
        title: rec.title || '',
        impact_label: rec.impact || '',
      });
      if (res.data.status === 'pending') {
        setRecs(prev => prev.map(r => r.id === id ? { ...r, status: 'applied' } : r));
        // "Onay Bekleyenler" rozeti hemen güncellensin (sekmeye girmeyi beklemeden): anında +1, ardından sunucudan doğrula
        setPendingLogsCount(c => c + 1);
        refreshPendingCount();
        showToast(t('Onay kuyruğuna eklendi. Optimizasyonlar → Onay Bekleyenler sekmesinden onaylayabilirsin.'));
      } else if (res.data.status === 'unsupported') {
        showToast(res.data.message, false);
      } else {
        showToast(res.data.message || t('Uygulama sırasında hata oluştu.'), false);
      }
    } catch (e: any) {
      showToast(e.response?.data?.detail || t('Uygulama sırasında hata oluştu.'), false);
    } finally { setApplying(null); }
  };

  const handleReject = (id: string) => {
    const rec = recs.find(r => r.id === id);
    setRecs(prev => prev.map(r => r.id === id ? { ...r, status: 'rejected' } : r));
    showToast(t('Öneri reddedildi.'));
    // Kalıcı: reddedilen öneri (AI önerilerinde aynı bulgu) bir daha getirilmez
    axios.post(`${API}/optimize/recommendations/dismiss`, { client_id: clientId, rec_id: id, title: rec?.title || '', ad_ids: rec?.action?.ad_ids || [] }).catch(() => {});
  };

  const applied      = recs.filter(r => r.status === 'applied');
  const pending      = recs.filter(r => r.status === 'pending');
  const brandRoas    = brandSpend>0 ? brandRevenue/brandSpend : 0;
  const nonBrandRoas = nonBrandSpend>0 ? nonBrandRevenue/nonBrandSpend : 0;
  const roasSegSpend = roasSegment==='brand' ? brandSpend : roasSegment==='nonbrand' ? nonBrandSpend : blendedSpend;
  const roasSegValue = roasSegment==='brand' ? brandRoas : roasSegment==='nonbrand' ? nonBrandRoas : blendedRoas;
  const totalAll     = recs.reduce((s, r) => s + (r.impact_value||0), 0);
  const totalApplied = applied.reduce((s, r) => s + (r.impact_value||0), 0);
  const applyRate    = totalAll > 0 ? Math.round((totalApplied/totalAll)*100) : 0;

  const filtered = recs.filter(r =>
    (platFilter === 'all' || r.platform === platFilter) &&
    (statusFilter === 'all' || r.status === statusFilter)
  );

  if (loading) return <div className="py-pageload"><PoyaisSpinner size={38} pad={0}/></div>;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

      {toast && (
        <div style={{ position:'fixed', bottom:32, right:32, zIndex:9999,
          background: toast.ok ? T.green : T.red, color:'#fff',
          padding:'12px 20px', borderRadius:12, boxShadow:'0 12px 32px rgba(20,22,43,.25)', maxWidth:'min(460px, calc(100vw - 32px))', lineHeight:1.45,
          fontSize:13, fontWeight:600 }}>
          {toast.msg}
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(170px,1fr))', gap:14 }}>
        {[
          { label:t('Ort. ROAS'),     value: roasSegSpend>0?`${roasSegValue.toFixed(2)}x`:'—', bg:'var(--py-green-tint)', fg:T.greenText, Ic:Icon.target, seg:true },
          { label:t('Toplam Harcama'),value: blendedSpend>0?fmt(blendedSpend):'—',            bg:'var(--py-amber-tint-alt)', fg:T.accentDeep, Ic:Icon.chart },
          { label:t('Aktif Öneri'),   value: String(pending.length),                          bg:'var(--py-blue-tint)', fg:T.blueText, Ic:Icon.bellAlert },
          { label:t('Uygulanan'),     value: String(applied.length),                          bg:'var(--py-purple-tint)', fg:T.purpleText, Ic:Icon.check },
        ].map((k:any, i) => (
          <div key={i} style={{ border:`1px solid ${T.border}`, borderRadius:14, background:T.surface, padding:'18px 19px' }}>
            <div style={{ display:'flex', alignItems:'center', gap:9, marginBottom:12 }}>
              <span style={{ width:26, height:26, borderRadius:8, background:k.bg, color:k.fg, display:'flex', alignItems:'center', justifyContent:'center' }}><k.Ic size={14} sw={2}/></span>
              {k.seg ? (
                <div style={{ position:'relative' }}>
                  <button onClick={() => setRoasMenuOpen(o => !o)} style={{ display:'flex', alignItems:'center', gap:3, border:'none', background:'none', padding:0, cursor:'pointer', fontFamily:'inherit' }}>
                    <span style={{ fontSize:12.5, fontWeight:600, color:'var(--py-text-mid-alt)' }}>{k.label}</span>
                    <Icon.chevron size={11} sw={2.4} color="var(--py-text-mid-alt)"/>
                  </button>
                  {roasMenuOpen && (<>
                    <div onClick={() => setRoasMenuOpen(false)} style={{ position:'fixed', inset:0, zIndex:10 }}/>
                    <div style={{ position:'absolute', top:'100%', left:0, marginTop:6, background:T.surface, border:`1px solid ${T.border}`, borderRadius:10, boxShadow:'0 8px 24px rgba(20,22,43,.14)', padding:4, zIndex:11, minWidth:126 }}>
                      {(['overall','brand','nonbrand'] as const).map(s => (
                        <button key={s} onClick={() => { setRoasSegment(s); setRoasMenuOpen(false); }} style={{ display:'block', width:'100%', textAlign:'left', border:'none', borderRadius:6, padding:'6px 10px', fontSize:12, fontWeight:600, fontFamily:'inherit', cursor:'pointer',
                          background: roasSegment===s ? 'var(--py-track-bg)' : 'transparent', color:'var(--py-text)' }}>
                          {s==='overall'?t('Genel'):s==='brand'?t('Marka'):t('Marka Dışı')}
                        </button>
                      ))}
                    </div>
                  </>)}
                </div>
              ) : (
                <span style={{ fontSize:12.5, fontWeight:600, color:'var(--py-text-mid-alt)' }}>{k.label}</span>
              )}
            </div>
            <div style={{ fontSize:24, fontWeight:800, fontFamily:MONO, letterSpacing:'-.02em' }}>{k.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display:'flex', gap:3, background:'var(--py-track-bg)', padding:3, borderRadius:10, width: isMobile ? '100%' : 'fit-content' }}>
        {(['oneriler','onay'] as const).map(v => (
          <button key={v} onClick={() => setView(v)} style={{
            padding:'7px 15px', borderRadius:8, border:'none', cursor:'pointer', fontSize:12.5, fontWeight: view===v?700:600,
            background: view===v ? T.surface : 'transparent', color: view===v ? T.text : 'var(--py-text-mid-alt)',
            boxShadow: view===v ? '0 1px 2px rgba(20,22,43,.1)' : 'none',
            display:'flex', alignItems:'center', gap:6,
          }}>
            {v==='oneriler' ? t('Öneriler') : t('Onay Bekleyenler')}
            {v==='onay' && pendingLogsCount > 0 && (
              <span style={{ fontSize:10.5, fontWeight:800, minWidth:16, height:16, padding:'0 4px', borderRadius:999,
                background:T.red, color:'#fff', display:'inline-flex', alignItems:'center', justifyContent:'center' }}>
                {pendingLogsCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {view === 'onay' ? (
        <PendingApprovals clientId={clientId} showToast={showToast} onCountChange={setPendingLogsCount} hasRule={false} />
      ) : (
      <>
      <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' as const, flexDirection: isMobile ? 'column' as const : 'row' as const }}>
        <div style={{ display:'flex', gap:3, background:'var(--py-track-bg)', padding:3, borderRadius:10, width: isMobile ? '100%' : 'auto' }}>
          {(['all','google','meta','ga4'] as const).map(p => {
            const labels: Record<string,string> = { all:t('Tümü'), google:t('Google'), meta:t('Meta'), ga4:'GA4' };
            return (
              <button key={p} onClick={() => setPlatFilter(p)} style={{
                padding:'6px 13px', borderRadius:8, border:'none', cursor:'pointer', fontSize:12, fontWeight: platFilter===p?700:600,
                background: platFilter===p ? T.surface : 'transparent',
                color: platFilter===p ? T.text : 'var(--py-text-mid-alt)',
                boxShadow: platFilter===p ? '0 1px 2px rgba(20,22,43,.1)' : 'none',
              }}>{labels[p]}</button>
            );
          })}
        </div>
        <div style={{ display:'flex', gap:3, background:'var(--py-track-bg)', padding:3, borderRadius:10, width: isMobile ? '100%' : 'auto' }}>
          {(['all','pending','applied','rejected'] as const).map(s => {
            const labels: Record<string,string> = { all:t('Tümü'), pending:t('Bekleyen'), applied:t('Uygulanan'), rejected:t('Reddedilen') };
            return (
              <button key={s} onClick={() => setStatusFilter(s)} style={{
                padding:'6px 13px', borderRadius:8, border:'none', cursor:'pointer', fontSize:12, fontWeight: statusFilter===s?700:600,
                background: statusFilter===s ? T.surface : 'transparent',
                color: statusFilter===s ? T.text : 'var(--py-text-mid-alt)',
                boxShadow: statusFilter===s ? '0 1px 2px rgba(20,22,43,.1)' : 'none',
              }}>{labels[s]}</button>
            );
          })}
        </div>
        <span style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:12 }}>
          <span style={{ fontSize:12, color:T.textDim }}>{filtered.length} {t('Öneri')}</span>
          <button onClick={refreshRecs} disabled={refreshing} style={{ padding:'9px 16px', borderRadius:11, border:'none', background:T.accent, color:'#fff', fontSize:13, fontWeight:700, cursor: refreshing ? 'default' : 'pointer', opacity: refreshing ? 0.7 : 1, fontFamily:'inherit' }}>
            {refreshing ? t('Yenileniyor…') : t('Yenile')}
          </button>
        </span>
      </div>

      <div style={{ display:'flex', flexDirection:'column' as const, gap:10 }}>
        {filtered.length === 0 && (
          <div style={{ border:`1px solid ${T.border}`, borderRadius:16, background:T.surface, padding:'48px 24px', textAlign:'center' as const }}>
            <div style={{ fontSize:13.5, fontWeight:700, marginBottom:6 }}>{t('Öneri bulunamadı')}</div>
            <div style={{ fontSize:12.5, color:T.textDim }}>{t('Bu filtre için görüntülenecek öneri yok.')}</div>
          </div>
        )}
        {filtered.map(rec => {
          const pc = PRIO[rec.priority] || PRIO.medium;
          const pl = PLAT[rec.platform] || { label: rec.platform, bg:'var(--py-track-bg)', fg:'var(--py-text-mid-alt)' };
          const isApplying = applying === rec.id;
          const isExpanded = expanded === rec.id;
          return (
            <div key={rec.id} style={{ border:`1px solid ${isExpanded?pc.fg:T.border}`, borderRadius:14, background:T.surface, overflow:'hidden' }}>
              <div style={{ padding: isMobile ? '13px 15px' : '14px 18px', display:'flex', alignItems:'flex-start', gap:14, cursor:'pointer' }}
                onClick={() => setExpanded(isExpanded ? null : rec.id)}>
                <div style={{ width:34, height:34, borderRadius:10, flexShrink:0, background:pc.bg, color:pc.fg, display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <Icon.bolt size={17} sw={1.9}/>
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:6, flexWrap:'wrap' as const }}>
                    <Pill bg={pc.bg} fg={pc.fg}>{t(pc.label)} {t('Öncelik')}</Pill>
                    <Pill bg={pl.bg} fg={pl.fg}>{pl.label}</Pill>
                    {/* Rozet yalnızca açıklamayı Poyais One AI'ın yazdığı kartlarda (backend description_rule'u bu durumda doldurur) */}
                    {rec.ai_reviewed && rec.description_rule && (
                      <span title={t('Açıklama Poyais One AI tarafından yazıldı')} style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'3px 9px', borderRadius:999, fontSize:10.5, fontWeight:800,
                        background:`color-mix(in srgb, ${T.accent} 14%, transparent)`, color:'var(--py-amber-deep-text)', letterSpacing:'0.02em' }}>
                        <Icon.sparkle size={11} sw={2}/>Poyais One AI
                      </span>
                    )}
                    {rec.status==='applied'  && <Pill bg="var(--py-green-tint)" fg={T.greenText}>{t('Onaya Gönderildi')}</Pill>}
                    {rec.status==='rejected' && <Pill bg="var(--py-track-bg)" fg="var(--py-text-mid-alt)">{t('Reddedildi')}</Pill>}
                  </div>
                  <div style={{ fontSize: isMobile ? 13 : 13.5, fontWeight:800, marginBottom:4, lineHeight:1.4 }}>{rec.title}</div>
                  <div style={{ fontSize:12.5, color:'var(--py-text-mid-alt)', lineHeight:1.5 }}>{rec.description?.slice(0,120)}{rec.description?.length>120?'…':''}</div>
                  {rec.track_record && typeof rec.track_record.median_roas_lift_pct === 'number' && (
                    <div style={{ fontSize:11.5, color:T.textDim, marginTop:6, lineHeight:1.5 }}>
                      {t('Geçmiş Etki')}: {rec.track_record.median_roas_lift_pct > 0 ? '+' : ''}{Math.round(rec.track_record.median_roas_lift_pct)}% ROAS
                      {' · '}{Math.round((rec.track_record.improved_share || 0) * 100)}% {t('iyileşti')} ({rec.track_record.n} {t('ölçüm')}{rec.track_record.scope === 'all' ? ` · ${t('tüm müşteriler')}` : ''}).
                    </div>
                  )}
                </div>
                {/* Tek biçim: eski (kural) ve yeni (AI) öneriler sağ üstte aynı sade satırı gösterir */}
                <div style={{ flexShrink:0, textAlign:'right' as const, display: isMobile ? 'none' : 'block', fontSize:11.5, color:T.textDim, lineHeight:1.5, whiteSpace:'nowrap' as const, alignSelf:'flex-start' }}>
                  {rec.impact && rec.impact !== '—' ? rec.impact : ''}
                </div>
              </div>

              {isExpanded && (
                <div style={{ borderTop:`1px solid ${T.tableDivider}`, padding:'14px 18px', background:'var(--py-subtle-bg)', display:'flex', flexDirection:isMobile?'column' as const:'row' as const, gap:14 }}>
                  <div style={{ flex:1 }}>
                    {rec.description && (
                      <div style={{ marginBottom:10 }}>
                        <div style={{ fontSize:10.5, fontWeight:800, letterSpacing:'.09em', textTransform:'uppercase' as const, color:'var(--py-text-mid-alt)', marginBottom:5 }}>{t('Açıklama')}</div>
                        <div style={{ fontSize:12.5, color:T.textMid, lineHeight:1.6 }}>{rec.description}</div>
                      </div>
                    )}
                    {rec.what_will_change && (
                      <div style={{ marginBottom:10 }}>
                        <div style={{ fontSize:10.5, fontWeight:800, letterSpacing:'.09em', textTransform:'uppercase' as const, color:'var(--py-text-mid-alt)', marginBottom:5 }}>{t('Ne Yapacak?')}</div>
                        <div style={{ fontSize:12.5, color:T.textMid, lineHeight:1.6 }}>{rec.what_will_change}</div>
                      </div>
                    )}
                    {(rec.action?.type==='target_roas' || rec.action?.type==='budget_update') && (
                      <div style={{ display:'flex', gap:8, alignItems:'center', marginTop:8 }}>
                        <span style={{ fontSize:11, background:'var(--py-red-tint)', color:T.redText, padding:'3px 9px', borderRadius:7, fontFamily:MONO, fontWeight:700 }}>
                          {rec.action.type==='budget_update'?'₺':''}{rec.action.current}
                        </span>
                        <span style={{ color:T.textDim }}>→</span>
                        <span style={{ fontSize:11, background:'var(--py-green-tint)', color:T.greenText, padding:'3px 9px', borderRadius:7, fontFamily:MONO, fontWeight:700 }}>
                          {rec.action.type==='budget_update'?'₺':''}{rec.action.suggested}
                        </span>
                      </div>
                    )}
                    {rec.action?.type==='allocation' && (
                      <div style={{ display:'flex', flexDirection:'column' as const, gap:6, marginTop:8 }}>
                        {[
                          { label:t('Kaynak'), plat:rec.action.from_platform, name:rec.action.from_campaign_name, cur:rec.action.from_current, sug:rec.action.from_suggested, down:true },
                          { label:t('Hedef'),  plat:rec.action.to_platform,   name:rec.action.to_campaign_name,   cur:rec.action.to_current,   sug:rec.action.to_suggested,   down:false },
                        ].map((row, i) => {
                          const pl = PLAT[row.plat] || { label: row.plat, bg:'var(--py-track-bg)', fg:'var(--py-text-mid-alt)' };
                          return (
                            <div key={i} style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' as const }}>
                              <span style={{ fontSize:10, fontWeight:800, color:'var(--py-text-mid-alt)', width:38, flexShrink:0 }}>{row.label}</span>
                              <Pill bg={pl.bg} fg={pl.fg}>{pl.label}</Pill>
                              <span style={{ fontSize:12, fontWeight:700, color:T.textMid }}>{row.name}</span>
                              <span style={{ fontSize:11, background: row.down?'var(--py-red-tint)':'var(--py-green-tint)', color: row.down?T.redText:T.greenText, padding:'3px 9px', borderRadius:7, fontFamily:MONO, fontWeight:700, marginLeft:'auto' }}>
                                ₺{row.cur} → ₺{row.sug}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {rec.status==='pending' && (
                    <div style={{ display:'flex', flexDirection:'column' as const, gap:8, flexShrink:0, width:isMobile?'100%':160 }}>
                      <button disabled={isApplying} onClick={()=>handleApply(rec.id)}
                        style={{ padding:'10px 0', borderRadius:10, border:'none', background:isApplying?T.borderSoft:T.accent, color:'var(--py-accent-ink)', fontSize:12.5, fontWeight:800, cursor:isApplying?'default':'pointer' }}>
                        {isApplying ? '···' : t('Uygula')}
                      </button>
                      <button onClick={()=>handleReject(rec.id)}
                        style={{ padding:'9px 0', borderRadius:10, border:'1px solid var(--py-red-tint)', background:'var(--py-red-tint)', color:T.redText, fontSize:12.5, fontWeight:700, cursor:'pointer' }}>
                        {t('Reddet')}
                      </button>
                    </div>
                  )}
                  {rec.status==='applied' && (
                    <button onClick={()=>setRecs(prev=>prev.map(r=>r.id===rec.id?{...r,status:'pending'}:r))}
                      style={{ flexShrink:0, padding:'9px 16px', borderRadius:10, border:'1px solid var(--py-purple-tint)', background:'var(--py-purple-tint)', color:T.purpleText, fontSize:12.5, fontWeight:700, cursor:'pointer', height:'fit-content' }}>
                      {t('Geri Al')}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {totalAll > 0 && (
        <div style={{ border:`1px solid ${T.border}`, borderRadius:16, background:T.surface, padding:'16px 18px', display:'flex', alignItems:'center', gap:16 }}>
          <div style={{ width:34, height:34, borderRadius:10, background:'var(--py-amber-tint-alt)', color:T.accentDeep, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            <Icon.sparkle size={17} sw={1.9}/>
          </div>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:10.5, fontWeight:800, letterSpacing:'.09em', textTransform:'uppercase' as const, color:'var(--py-text-mid-alt)', marginBottom:6 }}>{t('Toplam Tahmini Etki')}</div>
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <span style={{ fontSize:18, fontWeight:800, color:T.accentDeep, fontFamily:MONO }}>{fmt(totalAll)}</span>
              <div style={{ flex:1, height:4, background:'var(--py-track-bg)', borderRadius:2, overflow:'hidden' }}>
                <div style={{ height:'100%', background:T.accent, width:`${Math.min(100,applyRate)}%`, transition:'width .8s ease', borderRadius:2 }}/>
              </div>
              <span style={{ fontSize:11, color:T.textDim, whiteSpace:'nowrap' as const }}>{fmt(totalApplied)} {t('uygulandı')} ({applyRate}%)</span>
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}

const ACTION_TYPE_LABEL_TR: Record<string,string> = {
  increase_budget: 'Bütçe Artışı', decrease_budget: 'Bütçe Azaltma', pause_campaign: 'Kampanya Duraklat',
  pause_ad: 'Kampanya Duraklat', enable_campaign: 'Kampanya Etkinleştir', target_roas: 'Hedef ROAS Güncelle',
  update_target_roas: 'Hedef ROAS Güncelle', reallocate_budget: 'Bütçe Aktarımı',
};

// hasRule=false → yalnızca Optimizasyonlar'ın manuel "Uygula" kayıtları (rule_id yok).
// hasRule=true  → yalnızca Otomasyon Şablonları'nın scheduler'dan gelen kural bazlı onayları.
export function PendingApprovals({ clientId, showToast, onCountChange, hasRule, emptyTitle, emptyHint }: { clientId: number; showToast: (msg: string, ok?: boolean) => void; onCountChange?: (n: number) => void; hasRule?: boolean; emptyTitle?: string; emptyHint?: string }) {
  const { t, lang } = useLang();
  const [logs, setLogs] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState<number|null>(null);

  const load = () => {
    setLoading(true);
    axios.get(`${API}/automation/logs`, { params: { client_id: clientId, status: 'pending', limit: 100, ...(hasRule !== undefined ? { has_rule: hasRule } : {}) } })
      .then(r => setLogs(r.data)).catch(() => setLogs([])).finally(() => setLoading(false));
  };
  React.useEffect(() => { load(); }, [clientId]);
  React.useEffect(() => { onCountChange?.(logs.length); }, [logs]);

  const resolve = async (logId: number, status: 'approved'|'rejected') => {
    setBusy(logId);
    try {
      const res = await axios.patch(`${API}/automation/logs/${logId}/status`, { status });
      setLogs(prev => prev.filter(l => l.id !== logId));
      if (status === 'approved') {
        const ar = res.data.apply_result;
        showToast(ar?.message || t('Onaylandı ve uygulandı.'), ar?.status !== 'error');
      } else {
        showToast(t('Öneri reddedildi.'));
      }
    } catch (e: any) {
      showToast(e.response?.data?.detail || t('İşlem sırasında hata oluştu.'), false);
    } finally { setBusy(null); }
  };

  if (loading) return <PoyaisSpinner size={36} pad={24}/>;
  if (logs.length === 0) return (
    <div style={{ border:`1px solid ${T.border}`, borderRadius:16, background:T.surface, padding:'48px 24px', textAlign:'center' as const }}>
      <div style={{ fontSize:13.5, fontWeight:700, marginBottom:6 }}>{emptyTitle || t('Onay bekleyen öneri yok')}</div>
      <div style={{ fontSize:12.5, color:T.textDim }}>{emptyHint || t('Öneriler sekmesinden bir aksiyon uyguladığında burada onay bekleyecek.')}</div>
    </div>
  );

  return (
    <div style={{ display:'flex', flexDirection:'column' as const, gap:10 }}>
      {logs.map(log => {
        // Backend action_taken'ı ham JSON string olarak döndürüyor (App.tsx'in
        // diğer log tüketicileri de aynı şekilde JSON.parse ediyor)
        let at: any = {};
        try { at = typeof log.action_taken === 'string' ? JSON.parse(log.action_taken) : (log.action_taken || {}); } catch { at = {}; }
        const campaigns = at.campaigns || [];
        const isAlloc = at.type === 'reallocate_budget';
        const platLabel = isAlloc
          ? `${at.from?.platform==='meta'?'Meta':'Google'} Ads → ${at.to?.platform==='meta'?'Meta':'Google'} Ads`
          : (log.template_slug || '').startsWith('m') ? t('Meta') + ' Ads' : 'Google Ads';
        return (
          <div key={log.id} style={{ border:`1px solid ${T.border}`, borderRadius:14, background:T.surface, padding:'14px 18px', display:'flex', alignItems:'flex-start', gap:14, flexWrap:'wrap' as const }}>
            <div style={{ width:34, height:34, borderRadius:10, flexShrink:0, background:'var(--py-amber-tint-alt)', color:T.amberText, display:'flex', alignItems:'center', justifyContent:'center' }}>
              <Icon.bellAlert size={17} sw={1.9}/>
            </div>
            <div style={{ flex:1, minWidth:200 }}>
              <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:6, flexWrap:'wrap' as const }}>
                <Pill bg="var(--py-amber-tint-alt)" fg={T.amberText}>{t(ACTION_TYPE_LABEL_TR[at.type] || at.type)}</Pill>
                <Pill bg="var(--py-track-bg)" fg="var(--py-text-mid-alt)">{platLabel}</Pill>
              </div>
              <div style={{ fontSize:13.5, fontWeight:800, marginBottom:4 }}>{at.title || t('Optimizasyon önerisi')}</div>
              <div style={{ fontSize:12, color:'var(--py-text-mid-alt)' }}>
                {isAlloc
                  ? `${at.from?.campaign_name || at.from?.campaign_id || '—'} (-%${at.from?.amount_pct ?? 20}) → ${at.to?.campaign_name || at.to?.campaign_id || '—'} (+%${at.to?.amount_pct ?? 20})`
                  : (campaigns.map((c: any) => c.name || c.id).join(', ') || '—')}
                {at.amount_pct ? ` · %${at.amount_pct}` : ''}
                {at.target_roas ? ` · ${at.target_roas}x` : ''}
              </div>
              <div style={{ fontSize:11, color:T.textDim, marginTop:4 }}>{new Date(log.triggered_at.replace(' ','T')+'Z').toLocaleString(lang==='en'?'en-US':'tr-TR')}</div>
            </div>
            <div style={{ display:'flex', gap:8, flexShrink:0 }}>
              <button disabled={busy===log.id} onClick={() => resolve(log.id, 'approved')}
                style={{ padding:'9px 16px', borderRadius:10, border:'none', background: busy===log.id?T.borderSoft:T.accent, color:'var(--py-accent-ink)', fontSize:12.5, fontWeight:800, cursor: busy===log.id?'default':'pointer' }}>
                {busy===log.id ? '···' : t('Onayla')}
              </button>
              <button disabled={busy===log.id} onClick={() => resolve(log.id, 'rejected')}
                style={{ padding:'9px 16px', borderRadius:10, border:'1px solid var(--py-red-tint)', background:'var(--py-red-tint)', color:T.redText, fontSize:12.5, fontWeight:700, cursor: busy===log.id?'default':'pointer' }}>
                {t('Reddet')}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
